
import React, { useRef, useEffect } from 'react';
import MessageItem from './MessageItem';
import SuggestedQuestions from './SuggestedQuestions';
import LoadingIndicator from './LoadingIndicator';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface ChatMessageListProps {
  messages: Message[];
  isLoading: boolean;
  showSuggestions: boolean;
  handleSuggestedQuestion: (question: string) => void;
  autoScroll: boolean;
  setAutoScroll: (value: boolean) => void;
  popularQuestions?: string[];
}

const ChatMessageList: React.FC<ChatMessageListProps> = ({ 
  messages, 
  isLoading, 
  showSuggestions, 
  handleSuggestedQuestion,
  autoScroll,
  setAutoScroll,
  popularQuestions = []
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messages.length > 0 && autoScroll) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [messages, autoScroll]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (container.scrollHeight - container.scrollTop > container.clientHeight + 100) {
        setAutoScroll(false);
      }
      
      if (container.scrollHeight - container.scrollTop <= container.clientHeight + 10) {
        setAutoScroll(true);
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [setAutoScroll]);

  return (
    <div 
      ref={messagesContainerRef}
      className="flex-grow overflow-y-auto p-4 space-y-4 max-h-[400px] scroll-smooth"
    >
      {messages.map((msg, index) => (
        <MessageItem
          key={index}
          message={msg}
          isLastMessage={index === messages.length - 1}
          messageRef={messagesEndRef}
        />
      ))}
      
      {showSuggestions && messages.length === 0 && (
        <SuggestedQuestions 
          questions={[
            "How do I embed my form on WordPress?",
            "What can I advertise during Test the Waters?",
            "How do I track my investor leads?",
            "What are the Regulation CF requirements?",
          ]}
          onSelectQuestion={handleSuggestedQuestion}
        />
      )}
      
      {popularQuestions.length > 0 && messages.length > 0 && (
        <div className="mt-4 bg-gray-100 p-4 rounded-lg">
          <h3 className="text-sm font-semibold mb-2 text-gray-700">Most Popular Questions</h3>
          <div className="space-y-2">
            {popularQuestions.map((question, index) => (
              <div 
                key={index} 
                className="text-sm text-gray-600 cursor-pointer hover:text-gray-800 transition-colors"
                onClick={() => handleSuggestedQuestion(question)}
              >
                {question}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {isLoading && <LoadingIndicator />}
    </div>
  );
};

export default ChatMessageList;
